import {
  ArrowUpTrayIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import { Alert, AlertDescription } from "@/core/components/ui/alert/alert";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { FileUpload } from "@/core/components/ui/file-upload";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useUploadUsers } from "@/business/query";
import { CSVValidationError } from "@/core/utils/csv-validation";
import {
  validateTechnicianCSVFile,
  processValidatedTechnicianCSV,
} from "@/core/utils/technician-csv";

interface TechnicianUploadModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: () => void;
  businessId?: string;
}

interface UploadResult {
  successCount: number;
  failedRows?: Array<{
    row: Record<string, string>;
    errors: string;
  }>;
}

export const TechnicianUploadModal = ({
  isOpen,
  onOpenChange,
  onComplete,
  businessId,
}: TechnicianUploadModalProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [csvValidationErrors, setCsvValidationErrors] = useState<
    CSVValidationError[]
  >([]);

  const { mutate: uploadUsers } = useUploadUsers(businessId || null);

  const handleFileSelect = async (file: File) => {
    const validation = await validateTechnicianCSVFile(file);

    if (!validation.isValid) {
      setCsvValidationErrors(validation.errors);
      toast.error(
        "CSV file validation failed. Please check the file format and required fields.",
      );
      return;
    }

    setCsvValidationErrors([]);
    const processedFile = processValidatedTechnicianCSV(file, validation);
    setSelectedFile(processedFile);
    setUploadResult(null);
  };

  const handleUpload = () => {
    if (!selectedFile || !businessId) return;

    setIsUploading(true);
    uploadUsers(
      { file: selectedFile, welcomeEmail: true },
      {
        onSuccess: (response) => {
          const result: UploadResult = {
            successCount: response.success_count || 0,
            failedRows: response.failed_rows || [],
          };
          setUploadResult(result);
          setIsUploading(false);

          if (result.failedRows && result.failedRows.length > 0) {
            toast.error(
              `Uploaded ${result.successCount} technicians, but ${result.failedRows.length} failed`,
            );
          } else {
            toast.success(
              `Successfully uploaded ${result.successCount} technicians`,
            );
          }
        },
        onError: (error) => {
          setIsUploading(false);
          toast.error("Failed to upload technicians. Please try again.");
          console.error("Upload error:", error);
        },
      },
    );
  };

  const handleComplete = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setCsvValidationErrors([]);
    onComplete();
  };

  const handleClose = () => {
    if (!isUploading) {
      setSelectedFile(null);
      setUploadResult(null);
      setCsvValidationErrors([]);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-md"
        description="Upload a CSV file with technician details to add them to your account."
      >
        <DialogHeader>
          <DialogTitle>Upload Technicians</DialogTitle>
        </DialogHeader>

        <Stack gap="lg" fullWidth align="center">
          {!uploadResult ? (
            <>
              <Stack gap="lg" fullWidth className="px-6">
                <Typography variant="bodyW1" className="text-secondary-text">
                  Upload a CSV file with your technician information. The file
                  should include columns for first name, last name, email, and
                  phone number.{" "}
                  <span
                    className="cursor-pointer text-interactive-text"
                    onClick={() =>
                      window.open(
                        "/templates/technician_import_template.csv",
                        "_blank",
                      )
                    }
                  >
                    Download a template
                  </span>
                </Typography>

                {csvValidationErrors.length > 0 && (
                  <Alert variant="destructive">
                    <Stack gap="sm" fullWidth>
                      <Stack direction="row" gap="xs" align="center">
                        <ExclamationTriangleIcon className="text-error h-5 w-5" />
                        <Typography variant="subtitleW3">
                          CSV file validation failed
                        </Typography>
                      </Stack>
                      <AlertDescription>
                        <Stack gap="sm" fullWidth>
                          <Typography variant="bodyW2">
                            Please fix the following errors in your CSV file:
                          </Typography>
                          <ul className="list-disc space-y-1 pl-4">
                            {csvValidationErrors.map((error, index) => (
                              <li key={index}>
                                <Typography variant="captionW1">
                                  Row {error.row}, Column "{error.column}":{" "}
                                  {error.message}
                                  {error.value && ` (Value: "${error.value}")`}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </Stack>
                      </AlertDescription>
                    </Stack>
                  </Alert>
                )}

                <FileUpload
                  value={selectedFile ? [selectedFile] : []}
                  onChange={(files) => {
                    const file = files[0];
                    if (file) {
                      void handleFileSelect(file);
                    } else {
                      setSelectedFile(null);
                    }
                  }}
                  accept=".csv"
                  fileTypeName="CSV"
                  disabled={isUploading}
                  className="w-full"
                />
              </Stack>

              <DialogFooter className="w-full">
                <Button
                  variant="secondary"
                  onClick={handleClose}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={!selectedFile || csvValidationErrors.length > 0}
                  loading={isUploading}
                  leftIcon={<ArrowUpTrayIcon className="h-4 w-4" />}
                >
                  Upload
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <Stack gap="md" fullWidth className="px-4">
                <Typography variant="subtitleW1" className="w-full text-center">
                  Upload Complete!
                </Typography>

                <Stack gap="sm" align="center" fullWidth>
                  <Typography variant="titleW2" className="text-primary">
                    {uploadResult.successCount}
                  </Typography>
                  <Typography variant="bodyW1" className="text-secondary-text">
                    technicians uploaded successfully
                  </Typography>
                </Stack>

                {uploadResult.failedRows &&
                  uploadResult.failedRows.length > 0 && (
                    <Stack gap="sm" fullWidth>
                      <Typography
                        variant="bodyW1"
                        className="text-center text-red-600"
                      >
                        {uploadResult.failedRows.length} rows failed to upload
                      </Typography>
                      <div className="max-h-32 overflow-y-auto text-sm text-red-600">
                        {uploadResult.failedRows.map((row, index) => (
                          <div key={index} className="rounded bg-red-50 p-2">
                            <div>Row: {Object.values(row.row).join(", ")}</div>
                            <div>Error: {row.errors}</div>
                          </div>
                        ))}
                      </div>
                    </Stack>
                  )}
              </Stack>

              <DialogFooter>
                <Button onClick={handleComplete} fullWidth>
                  Continue
                </Button>
              </DialogFooter>
            </>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
